/** Live chat commerce offers, matching Django's TreatmentOffer (backend/api/commerce_offers.py). */

import {
  decodeProgramOffer,
  decodeSavedProgramOffer,
  type ProgramCardBlock,
  type SavedProgramCardBlock,
  type SavedProgramOffer,
} from "@/services/commerceProgramOffers";

const OFFER_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;
// At most twelve integer digits and exactly two decimals, no sign or exponent.
const PRICE_PATTERN = /^[0-9]{1,12}\.[0-9]{2}$/;

export interface AddToCartAction {
  type: "add_to_cart";
  item: {
    item_type: "individual-treatment";
    treatment_product_id: number;
    quantity: 1;
    is_subscription: false;
  };
}

export interface TreatmentOffer {
  offer_id: string;
  kind: "treatment";
  product_slug: string;
  treatment_product_id: number;
  name: string;
  description: string;
  image_url: string | null;
  pdp_slug: string | null;
  price: string;
  purchase_mode: "one_time";
  action: AddToCartAction;
}

export interface CommerceCardBlock {
  type: "commerce_card";
  schema_version: 1;
  cart_id: string | null;
  offer: TreatmentOffer;
}

/** What a restored card shows: no action, product IDs or stale price. */
export type SavedOffer = Pick<
  TreatmentOffer,
  "offer_id" | "name" | "description" | "image_url" | "pdp_slug"
>;

/** A card restored from history. Its own type, so it never reaches the live card. */
export interface SavedCommerceCardBlock {
  type: "saved_commerce_card";
  offer: SavedOffer;
}

type JsonObject = Record<string, unknown>;

const isObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === "string";

/** The offer with its kind filled in, or null for any malformed or unsupported offer. */
export function decodeTreatmentOffer(offer: unknown): TreatmentOffer | null {
  if (!isObject(offer) || !isObject(offer.action)) return null;
  const { action } = offer;
  const { item } = action;
  if (!isObject(item)) return null;
  const valid =
    // Offers without kind predate it and have this individual treatment shape.
    (offer.kind === undefined || offer.kind === "treatment") &&
    typeof offer.offer_id === "string" &&
    OFFER_ID_PATTERN.test(offer.offer_id) &&
    typeof offer.product_slug === "string" &&
    offer.product_slug !== "" &&
    Number.isSafeInteger(offer.treatment_product_id) &&
    (offer.treatment_product_id as number) > 0 &&
    typeof offer.name === "string" &&
    offer.name !== "" &&
    typeof offer.description === "string" &&
    isNullableString(offer.image_url) &&
    isNullableString(offer.pdp_slug) &&
    typeof offer.price === "string" &&
    PRICE_PATTERN.test(offer.price) &&
    offer.purchase_mode === "one_time" &&
    action.type === "add_to_cart" &&
    item.item_type === "individual-treatment" &&
    item.treatment_product_id === offer.treatment_product_id &&
    item.quantity === 1 &&
    item.is_subscription === false;
  return valid ? ({ ...offer, kind: "treatment" } as TreatmentOffer) : null;
}

/** The live card with its kind filled in, or null for any malformed or unsupported card. */
export function decodeCommerceCard(block: unknown): CommerceCardBlock | null {
  if (
    !isObject(block) ||
    block.type !== "commerce_card" ||
    block.schema_version !== 1 ||
    !isNullableString(block.cart_id)
  )
    return null;
  const offer = decodeTreatmentOffer(block.offer);
  return (
    offer && {
      type: "commerce_card",
      schema_version: 1,
      cart_id: block.cart_id,
      offer,
    }
  );
}

/** Only the display fields, whatever else the offer carries, or null when malformed. */
export function decodeSavedOffer(offer: unknown): SavedOffer | null {
  if (!isObject(offer)) return null;
  const { offer_id, kind, name, description, image_url, pdp_slug } = offer;
  if (
    (kind !== undefined && kind !== "treatment") ||
    typeof offer_id !== "string" ||
    !OFFER_ID_PATTERN.test(offer_id) ||
    typeof name !== "string" ||
    name === "" ||
    typeof description !== "string" ||
    !isNullableString(image_url) ||
    !isNullableString(pdp_slug)
  )
    return null;
  return { offer_id, name, description, image_url, pdp_slug };
}

/** A saved history card, read-only even if it still carries an action, or null when malformed. */
export function decodeSavedCommerceCard(
  block: unknown
): SavedCommerceCardBlock | null {
  if (
    !isObject(block) ||
    block.type !== "commerce_card" ||
    block.schema_version !== 1
  )
    return null;
  const offer = decodeSavedOffer(block.offer);
  return offer && { type: "saved_commerce_card", offer };
}

const isProgram = (
  block: unknown
): block is JsonObject & { offer: JsonObject } =>
  isObject(block) && isObject(block.offer) && block.offer.kind === "program";

/** A live card decoded by its offer's kind, or null when malformed or of an unknown kind. */
export function decodeLiveCard(
  block: unknown
): CommerceCardBlock | ProgramCardBlock | null {
  if (!isProgram(block)) return decodeCommerceCard(block);
  const offer = decodeProgramOffer(block.offer);
  return offer &&
    block.type === "commerce_card" &&
    block.schema_version === 1 &&
    isNullableString(block.cart_id)
    ? {
        type: "commerce_card",
        schema_version: 1,
        cart_id: block.cart_id,
        offer,
      }
    : null;
}

/** A saved card decoded by its offer's kind, always read-only, or null when malformed. */
export function decodeSavedCard(
  block: unknown
): SavedCommerceCardBlock | SavedProgramCardBlock | null {
  if (!isProgram(block)) return decodeSavedCommerceCard(block);
  const offer = decodeSavedProgramOffer(block.offer);
  return offer && block.type === "commerce_card" && block.schema_version === 1
    ? { type: "saved_program_card", offer }
    : null;
}

/** A restored block's display offer by the block's kind, or null for any other or malformed block. */
export const decodeSavedBlockOffer = (block: {
  type: string;
  offer?: unknown;
}): SavedOffer | SavedProgramOffer | null =>
  block.type === "saved_commerce_card"
    ? decodeSavedOffer(block.offer)
    : block.type === "saved_program_card"
      ? decodeSavedProgramOffer(block.offer)
      : null;
